/* eslint max-statements: ["error", {max: 25}] -- applies to entire file */
import { useEffect } from "react";

import isNil from "lodash/isNil";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { OnboardingScreenPath } from "@/constants/Navigation";
import { router } from "@/crossPlatform/expo-router";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useGetUsStatesQuery } from "@/store/api/userEnums";
import { useAppUserProxyPatientsSelector } from "@/store/reducers/appUser";
import OnboardingVerifyInfoPage from "@/ui/components/onboarding/OnboardingVerifyInfoPage";
import { AnalyticsSource } from "@/ui/components/profile/UserOverviewCard";
import { FullScreenSpinner } from "@/ui/core";
import { Box, ScreenContainer } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { getUserProfileProps } from "@/utils/profile/fullProfile.util";

const getUserOverviewCardProps = ({
  profile,
  usStates,
}: {
  profile: FullProfileResponse;
  usStates?: UsStateEnumOption[];
}) => {
  const {
    address,
    contactPreferences,
    fullName,
    gender,
    languages,
    phoneNumber,
    pronouns,
    selectedUserId,
    userType,
  } = getUserProfileProps({ profile, usStates });
  return {
    address,
    analyticsSource: AnalyticsSource.Onboarding,
    contactPreferences,
    fullName,
    gender,
    languages,
    phoneNumber,
    pronouns,
    selectedUserId,
    userType,
  };
};

const Screen = () => {
  const { showToast } = useToast();
  const { track, trackPageView } = useAnalytics();
  const { captureException } = useAppMonitor();

  useEffect(() => {
    trackPageView(OnboardingAnalyticsPageView.VerifyInformation);
  }, [trackPageView]);

  const onNextPress = () => {
    track(OnboardingAnalyticsEvent.NextButtonPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]:
        OnboardingAnalyticsPageView.VerifyInformation,
    });
    router.replace(OnboardingScreenPath.Notifications);
  };

  const {
    data: fullProfileData,
    error: fullProfileError,
    isError: isFullProfileError,
    isFetching: isFullProfileFetching,
    isLoading: isFullProfileLoading,
  } = useGetFullProfileQuery();
  const proxyPatients = useAppUserProxyPatientsSelector();

  const { data: usStates } = useGetUsStatesQuery();

  // if have no data, a spinner is displayed
  // if data is re-fetching, we also display a spinner so that the profile is not verified on stale/outdated data
  if (isFullProfileFetching || isFullProfileLoading) {
    return <FullScreenSpinner />;
  }
  const errorText = "Failed to load profile details";
  // cannot display edit profile if error getting full profile info
  if (isFullProfileError || !isNil(fullProfileError)) {
    captureException(fullProfileError);
    showToast({ text: errorText, variant: "error" });
    throw fullProfileError;
  }

  if (isNil(fullProfileData)) {
    const message =
      "Fetched user's full profile info from API but received empty data";
    captureException(new Error(message));
    showToast({ text: errorText, variant: "error" });
    return <FullScreenSpinner />;
  }

  return (
    <ScreenContainer
      backgroundColor={ColorToken.Primary10}
      isScrollView
      isStackLayout
    >
      <Box
        paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingTop={LayoutToken.OnboardingScreenTopPadding}
      >
        <OnboardingVerifyInfoPage
          onNextPress={onNextPress}
          proxyPatientProfileOverviews={proxyPatients?.map((profile) =>
            getUserOverviewCardProps({ profile, usStates }),
          )}
          selfProfileOverview={getUserOverviewCardProps({
            profile: fullProfileData,
            usStates,
          })}
        />
      </Box>
    </ScreenContainer>
  );
};

export { getUserOverviewCardProps };

// eslint-disable-next-line import-x/no-unused-modules -- File is used by expo-router file based navigation but TypeScript can not infer its usage
export default Screen;
